import { useEffect, useState } from "react";
import { Loader2, Search, Trash2 } from "lucide-react";
import { listAdminWorkspaces, deleteAdminWorkspace, type AdminWorkspaceRow, type Page } from "../../../features/admin/api/adminApi";
import { PageHeader, StatusPill, shortDate, money } from "./_shared";
import WorkspaceBillingEditor from "./WorkspaceBillingEditor";

export default function AdminWorkspaces() {
  const [data, setData] = useState<Page<AdminWorkspaceRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    listAdminWorkspaces({ page, search }).then(setData).catch(() => undefined).finally(() => setLoading(false));
  };
  useEffect(load, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (ws: AdminWorkspaceRow) => {
    if (!confirm(`Delete workspace "${ws.name}"? Its models/embeds metadata will be removed. This cannot be undone.`)) return;
    try {
      await deleteAdminWorkspace(ws.id);
      load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <>
      <PageHeader title="Workspaces" subtitle={data ? `${data.total} total` : undefined} />
      <div className="p-4 sm:p-6">
        <div className="relative mb-4 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search name or slug…"
            className="w-full rounded-lg border border-ink-200 py-2 pl-9 pr-3 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
          />
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            {(data?.rows ?? []).map((ws) => (
              <div key={ws.id} className="surface-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink-900">{ws.name}</p>
                    <p className="text-[11.5px] text-ink-400">
                      /{ws.slug} · {ws.owner ? `${ws.owner.name} (${ws.owner.email})` : "no owner"} · joined {shortDate(ws.createdAt)}
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-ink-400">
                      {ws.modelCount} models · {ws.memberCount} members{ws.storageEnabled ? " · BYO-S3" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={ws.billingType} />
                    <span className="text-[12px] text-ink-600">
                      {ws.plan ? `${ws.plan.name} · ${money(ws.plan.monthlyPrice)}/mo` : "No plan"}
                      {ws.planLapsed && <span className="ml-1 text-rose">(lapsed)</span>}
                    </span>
                    <button onClick={() => remove(ws)} className="rounded-lg p-1.5 text-ink-400 hover:text-rose" title="Delete workspace">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="mt-3">
                  <WorkspaceBillingEditor
                    workspaceId={ws.id}
                    billingType={ws.billingType}
                    planId={ws.plan?.id ?? null}
                    manualPlanEndsAt={ws.manualPlanEndsAt}
                    onSaved={load}
                  />
                </div>
              </div>
            ))}
            {data && data.rows.length === 0 && <p className="text-[12.5px] text-ink-400">No workspaces match.</p>}
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-[12.5px]">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span className="text-ink-500">Page {page} of {data.totalPages}</span>
            <button disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </>
  );
}
